import { FC } from "react";
import "./Help.css";
import Sprite from "./Sprite";

export interface HelpProps {
  onClose: () => void;
}

const Help: FC<HelpProps> = ({ onClose }) => {
  return (
    <div className="help-overlay">
      <div className="help-backdrop" onClick={onClose} />
      <div className="help-container">
        <div className="help-header">
          <h2>How to Play</h2>
          <button className="help-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="help-content">
          <section className="help-section">
            <h3>Goal</h3>
            <p>
              Survive as long as possible by strategically placing and combining
              buildings.
              <p></p>
              Three of the same card next to each other will combine into an
              upgraded version.
            </p>
          </section>

          <section className="help-section">
            <h3>Feedback</h3>
            <p>
              This game is in active development! Please send feedback, bug
              reports, or suggestions to{" "}
              <a
                href="mailto:tower3@compnski.com"
                style={{ color: "#4a90e2", textDecoration: "underline" }}
              >
                tower3@compnski.com
              </a>
              . Your input helps improve the game!
            </p>
          </section>

          <section className="help-section">
            <h3>Combining Buildings</h3>
            <p>
              Place three buildings of the same type and level next to each
              other to combine them into a single upgraded building. Higher
              level buildings provide better resources but often require more
              resources to operate and sometimes need other buildings to be
              placed first.
            </p>
            <div className="help-example-placeholder">
              <div
                className="help-example-box"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "20px",
                }}
              >
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <Sprite
                    filename="/images/playables.png"
                    x={1}
                    y={1}
                    width={32}
                    height={32}
                  />
                  <span style={{ fontSize: "20px" }}>+</span>
                  <Sprite
                    filename="/images/playables.png"
                    x={1}
                    y={1}
                    width={32}
                    height={32}
                  />
                  <span style={{ fontSize: "20px" }}>+</span>
                  <Sprite
                    filename="/images/playables.png"
                    x={1}
                    y={1}
                    width={32}
                    height={32}
                  />
                </div>
                <span style={{ fontSize: "24px", color: "#90ee90" }}>→</span>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Sprite
                    filename="/images/playables.png"
                    x={2}
                    y={1}
                    width={32}
                    height={32}
                  />
                  <span style={{ fontSize: "12px", marginTop: "4px" }}>
                    Shack
                  </span>
                </div>
              </div>
              <div
                style={{
                  textAlign: "center",
                  marginTop: "10px",
                  fontSize: "14px",
                  color: "#b0b0b0",
                }}
              >
                3 Tents combine into 1 Shack
              </div>
            </div>
          </section>

          <section className="help-section">
            <h3>Managing Your Hand</h3>
            <p>
              Your hand shows the cards available to place. The leftmost card
              (highlighted in green) is your active card for placement.{" "}
            </p>
            <p>Note: Cards can also combine in your hand!</p>
            <ul className="help-list">
              <li>
                <strong>Select a card:</strong> Click any card to swap and make
                it active
              </li>
              <li>
                <strong>Rearrange cards:</strong> Drag and drop cards to reorder
                them
              </li>
              <li>
                <strong>Draw new cards:</strong> Click the draw button when
                available
              </li>
              <li>
                <strong>Place buildings:</strong> Click on empty tiles with your
                active card selected
              </li>
            </ul>
            <div className="help-example-placeholder">
              <div className="help-example-box">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <img
                    src="/images/help_hand_combine_2.png"
                    alt="Example hand showing cards"
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "4px",
                    }}
                  />
                  <span style={{ fontSize: "14px", color: "#b0b0b0" }}>
                    Example hand - leftmost card (green border) is active.
                  </span>

                  <img
                    src="/images/help_hand_combine_3.png"
                    alt="Example hand showing cards"
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "4px",
                    }}
                  />
                  <span style={{ fontSize: "14px", color: "#b0b0b0" }}>
                    Swapping the plow for the tent combined all three into a
                    Garden.
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="help-section">
            <h3>Resources</h3>
            <p>
              Buildings generate or consume resources each turn. Money is the
              only resource that accumulates over time - all other resources
              (Ore, Food, Fuel, Fight) are per-turn values. Click the
              "Resources" button to see an overlay showing what each building
              produces or consumes. Note: Fight isn't used yet.
            </p>
          </section>

          <section className="help-section">
            <h3>Five Building Tracks</h3>
            <p>
              There are five different types of buildings, each with their own
              upgrade path:
            </p>
            <div className="help-example-placeholder">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: "15px",
                  padding: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Sprite
                    filename="/images/playables.png"
                    x={1}
                    y={2}
                    width={32}
                    height={32}
                  />
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>Food</div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Sprite
                    filename="/images/playables.png"
                    x={1}
                    y={1}
                    width={32}
                    height={32}
                  />
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>Pops</div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Sprite
                    filename="/images/playables.png"
                    x={1}
                    y={0}
                    width={32}
                    height={32}
                  />
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>Fuel</div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Sprite
                    filename="/images/playables.png"
                    x={1}
                    y={3}
                    width={32}
                    height={32}
                  />
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>Ore</div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Sprite
                    filename="/images/playables.png"
                    x={1}
                    y={4}
                    width={32}
                    height={32}
                  />
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>
                    Fight
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="help-section">
            <h3>Card Packs</h3>
            <p>
              Purchase card packs from the Shop to get more building cards. Each
              pack contains different combinations of buildings with higher
              packs costing more but containing better buildings. Packs 4 and 5
              aren't finished yet. Check the Shop to see what each pack
              contains.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Help;
